import { useState, useEffect, useMemo } from 'react';
import { Info, X, Lock, Ban } from 'lucide-react';
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { useAuth } from '../context/AuthContext';
import { useSearch } from '../context/SearchContext';
import { ProjectSearchBox } from '../components/ProjectSearchBox';
import { usePageFilters } from '../context/PageFiltersContext';
import { useAccent } from '../context/AccentContext';
import { api } from '../api/client';
import type { Project, GridColumn, GridRow } from '../types';
import { ProjectTimeline } from '../components/ProjectTimeline';
import { getDoneStatusIds } from '../utils/stages';
import { buildStageColors, buildStageGradient, getStageCursorPercent, getSecondaryStage } from '../utils/stageColors';
import type { SecondaryStage } from '../utils/stageColors';
import { useT } from '../i18n';
import { FilterSelect } from '../components/FilterSelect';
import { getExecutionHealth, HEALTH_TITLE_KEY, type ExecutionHealth } from '../utils/executionHealth';
import { projectDisplayCode } from '../utils/identifiers';
import { buildProjectLinkIndex, linkCounts, linksFor, projectLinksFor } from '../utils/projectLinks';
import type { ProjectLinkIndex } from '../utils/projectLinks';

// ── JSON helpers ──

function parseJSON<T>(val: any, fallback: T): T {
  if (!val) return fallback;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

function parseSponsor(p: Project): string {
  return parseJSON<any>(p.ownership, {}).sponsor || '—';
}

function parseUserGroup(p: Project): string {
  const ug = parseJSON<any>(p.ownership, {}).user_group;
  if (!ug) return '—';
  if (Array.isArray(ug)) return ug.join(', ') || '—';
  return ug;
}

function parseEffort(p: Project): string {
  return parseJSON<any>(p.resources_effort, {}).effort_size || '';
}

function parseTimeline(p: Project): string {
  return parseJSON<any>(p.resources_effort, {}).timeline || '';
}

function parseCollaborators(p: Project): { name: string; role: string; allocation: string }[] {
  return parseJSON<any>(p.resources_effort, {}).collaborators || [];
}

// Deliverables are {id, title, body} cards; legacy rows may still be a plain
// string array (kept working here — only the title is shown in this summary).
function parseDeliverables(p: Project): { id: string; title: string }[] {
  const arr = parseJSON<any[]>(p.deliverables, []);
  return arr.map((d, i) => typeof d === 'string' ? { id: d, title: d } : { id: d.id || `d${i}`, title: d.title || '' });
}

function parseValueAdded(p: Project): string[] {
  return parseJSON<string[]>(p.value_added, []);
}

function parseInfrastructure(p: Project): string[] {
  return parseJSON<any>(p.dependencies, {}).infrastructure || [];
}

// ── Detail Panel ──

function DetailPanel({ project, projects, onClose }: {
  project: Project;
  projects: Project[];
  onClose: () => void;
}) {
  const { t } = useT();
  const links = useMemo(() => projectLinksFor(project, projects), [project, projects]);
  const { blockers, blocked } = links;

  return (
    <div className="panel-overlay open" onClick={onClose}>
      <div className="panel-overlay-bg" />
      <div className="panel-slide" onClick={e => e.stopPropagation()}>
        <div className="panel-slide-head">
          <div className="panel-slide-title">{project.name}</div>
          <button className="panel-slide-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="panel-slide-body">
              <div className="detail-badges">
                {project.status_label && <span className="badge badge-accent">{project.status_label}</span>}
                {project.domain_label && <span className="badge">{project.domain_label}</span>}
                {project.level_label && <span className="badge">{project.level_label}</span>}
                {project.is_blocked && <span className="badge badge-err" title={project.blocked_reason || undefined}>{t('projectDetail.blockedBadge')}</span>}
              </div>

              {project.problem && (
                <div className="detail-section">
                  <div className="detail-label">{t('portfolio.problem')}</div>
                  <div className="detail-text">{project.problem}</div>
                </div>
              )}

              {project.goal && (
                <div className="detail-section">
                  <div className="detail-label">{t('portfolio.goal')}</div>
                  <div className="detail-text">{project.goal}</div>
                </div>
              )}

              {parseValueAdded(project).length > 0 && (
                <div className="detail-section">
                  <div className="detail-label">{t('portfolio.valueAdded')}</div>
                  <div className="detail-badges">
                    {parseValueAdded(project).map(v => <span key={v} className="badge">{v}</span>)}
                  </div>
                </div>
              )}

              {parseDeliverables(project).length > 0 && (
                <div className="detail-section">
                  <div className="detail-label">{t('portfolio.deliverables')}</div>
                  <ul className="detail-list">
                    {parseDeliverables(project).map((d) => <li key={d.id}>{d.title}</li>)}
                  </ul>
                </div>
              )}

              {(parseEffort(project) || parseTimeline(project) || parseCollaborators(project).length > 0) && (
                <div className="detail-section">
                  <div className="detail-label">{t('portfolio.resourcesEffort')}</div>
                  {parseEffort(project) && <div className="detail-row"><span className="detail-row-label">{t('portfolio.effort')}</span> {parseEffort(project)}</div>}
                  {parseTimeline(project) && <div className="detail-row"><span className="detail-row-label">{t('portfolio.timeline')}</span> {parseTimeline(project)}</div>}
                  {parseCollaborators(project).length > 0 && (
                    <div className="detail-collabs">
                      {parseCollaborators(project).map((c, i) => (
                        <div key={i} className="detail-collab">
                          <span>{c.name}</span>
                          {c.allocation && <span className="text-ink-3">{c.allocation}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="detail-section">
                <div className="detail-label">{t('portfolio.ownership')}</div>
                <div className="detail-row"><span className="detail-row-label">{t('portfolio.sponsor')}</span> {parseSponsor(project)}</div>
                <div className="detail-row"><span className="detail-row-label">{t('portfolio.pmoAccompanist')}</span> {parsePmoAccompanist(project) || '—'}</div>
                <div className="detail-row"><span className="detail-row-label">{t('portfolio.userGroup')}</span> {parseUserGroup(project)}</div>
              </div>

              {(blockers.length > 0 || blocked.length > 0 || parseInfrastructure(project).length > 0) && (
                <div className="detail-section">
                  <div className="detail-label">{t('portfolio.dependencies')}</div>
                  {blockers.length > 0 && (
                    <div className="detail-dep-group">
                      <div className="detail-row-label">{t('portfolio.enabledBy')}</div>
                      {blockers.map((l, i) => <div key={i} className="detail-dep-item" title={l.reason || undefined}>{l.name}</div>)}
                    </div>
                  )}
                  {blocked.length > 0 && (
                    <div className="detail-dep-group">
                      <div className="detail-row-label">{t('portfolio.enables')}</div>
                      {blocked.map((l, i) => <div key={i} className="detail-dep-item" title={l.reason || undefined}>{l.name}</div>)}
                    </div>
                  )}
                  {parseInfrastructure(project).length > 0 && (
                    <div className="detail-dep-group">
                      <div className="detail-row-label">{t('portfolio.infrastructure')}</div>
                      <div className="detail-badges">
                        {parseInfrastructure(project).map((inf, i) => <span key={i} className="badge">{inf}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* The history is for the people who are part of the project; others see its overview or card only. */}
              {(project.access_level ?? 'full') === 'full'
                ? <ProjectTimeline projectId={project.id} />
                : <div className="detail-section text-sm text-ink-3">{t(project.access_level === 'card' ? 'projectDetail.privateCardOnly' : 'projectDetail.overviewOnly')}</div>}
        </div>
      </div>
    </div>
  );
}

// ── Project Card ──

function parseSponsorFirstName(p: Project): string {
  const sponsor = parseJSON<any>(p.ownership, {}).sponsor || '';
  return sponsor.split(' ')[0];
}

function parsePmoAccompanist(p: Project): string {
  return parseJSON<any>(p.ownership, {}).pmo_accompanist || '';
}

type CardHighlight = 'needs' | 'enables' | 'both' | 'focused' | 'dimmed' | null;

function ProjectCard({ project, onInfo, highlight, onCardClick, isAdmin, stageGradient, cursorPercent, secondaryStage, health, linkIndex }: {
  project: Project;
  onInfo: (e: React.MouseEvent) => void;
  highlight: CardHighlight;
  onCardClick: () => void;
  isAdmin: boolean;
  stageGradient?: string;
  cursorPercent?: number | null;
  secondaryStage?: SecondaryStage;
  health?: ExecutionHealth | null;
  linkIndex?: ProjectLinkIndex | null;
}) {
  const { t } = useT();
  const { needs, blocks } = linkCounts(linkIndex ?? null, project.id);
  const sponsor = parseSponsorFirstName(project);
  const accompanist = parsePmoAccompanist(project);
  const hasBottom = needs > 0 || blocks > 0 || !!health;

  const hlClass = highlight ? ` project-card--${highlight}` : '';

  return (
    <div
      className={`project-card${hlClass}`}
      onClick={(e) => { e.stopPropagation(); onCardClick(); }}
    >
      <button
        className="project-card-info"
        onClick={onInfo}
        title={t('portfolio.viewDetails')}
      >
        <Info size={14} />
      </button>
      <div
        className="project-card-name"
        onClick={(e) => {
          e.stopPropagation();
          // A private project the user isn't part of can't be opened, only seen here.
          if (project.access_level === 'card') return;
          window.location.hash = `project?id=${project.id}&from=portfolio`;
        }}
      >
        {project.is_private && <span title={t('projects.privateBadge')} style={{ display: 'inline-flex' }}><Lock size={11} style={{ marginRight: 4, opacity: 0.5, flexShrink: 0, verticalAlign: 'middle' }} /></span>}
        {project.is_blocked && <span title={project.blocked_reason || t('projectDetail.blockedBadge')} style={{ display: 'inline-flex' }}><Ban size={11} style={{ marginRight: 4, color: 'var(--err)', flexShrink: 0, verticalAlign: 'middle' }} /></span>}
        {project.name}
        {sponsor && <span className="project-card-sponsor"> — {sponsor}</span>}
        <span className="project-card-code"> — {projectDisplayCode(project)}</span>
        {accompanist && <span className="project-card-accompanist"> — {t('common.pmoAccompanistAbbrev')} {accompanist}</span>}
      </div>
      {(hasBottom || stageGradient || secondaryStage) && (
        <div className="project-card-bottom-group">
          {hasBottom && (
            <div className="project-card-bottom">
              {health ? (
                <span
                  className={`project-card-health health-${health.level}`}
                  title={t(HEALTH_TITLE_KEY[health.level]) + (health.milestone ? ' — ' + health.milestone : '')}
                />
              ) : <span />}
              <div className="project-card-deps">
                {needs > 0 && <span className="project-card-dep dep-needs">{t('portfolio.needsCount', { n: needs })}</span>}
                {blocks > 0 && <span className="project-card-dep dep-blocks">{t('portfolio.blocksCount', { n: blocks })}</span>}
              </div>
            </div>
          )}
          {secondaryStage ? (
            <div className="project-card-stage-label">
              {secondaryStage === 'backlog' ? t('portfolio.backlog') : t('portfolio.deferred')}
            </div>
          ) : stageGradient ? (
            <div className="project-card-stage-bar" style={{ background: stageGradient }}>
              {cursorPercent != null && (
                <div className="project-card-stage-cursor" style={{ left: `${cursorPercent}%` }} />
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ── DnD Wrappers ──

function DraggableProjectCard(props: {
  project: Project;
  onInfo: (e: React.MouseEvent) => void;
  highlight: CardHighlight;
  onCardClick: () => void;
  isAdmin: boolean;
  stageGradient?: string;
  cursorPercent?: number | null;
  secondaryStage?: SecondaryStage;
  health?: ExecutionHealth | null;
  linkIndex?: ProjectLinkIndex | null;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `project-${props.project.id}`,
    data: { project: props.project },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-dnd-draggable
      data-dnd-dragging={isDragging || undefined}
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <ProjectCard {...props} />
    </div>
  );
}

function DroppableCell({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className="matrix-grid-cell"
      data-dnd-over={isOver || undefined}
    >
      {children}
    </div>
  );
}

// ── Portfolio Page ──

export function PortfolioPage() {
  const { t } = useT();
  const { isAdmin } = useAuth();
  const { searchTerm } = useSearch();
  const { accent } = useAccent();
  const { portfolioStageFilter: stageFilter, setPortfolioStageFilter: setStageFilter } = usePageFilters();
  const [projects, setProjects] = useState<Project[]>([]);
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [rows, setRows] = useState<GridRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailProject, setDetailProject] = useState<Project | null>(null);
  const [focusedProjectId, setFocusedProjectId] = useState<number | null>(null);
  const [pipelineColumns, setPipelineColumns] = useState<GridColumn[]>([]);
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  // Mount the stage selector into the left of the topbar (by the logo) via a portal.

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const project = event.active.data.current?.project as Project;
    if (project) {
      setActiveProject(project);
      setFocusedProjectId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveProject(null);
    const { active, over } = event;
    if (!over) return;

    const project = active.data.current?.project as Project;
    if (!project) return;

    const overId = String(over.id);
    if (!overId.startsWith('cell-')) return;

    const parts = overId.split('-');
    const targetLevelId = parts[1] === 'null' ? null : Number(parts[1]);
    const targetColumnId = parts[2] === 'null' ? null : Number(parts[2]);

    // No change = no-op
    if (targetColumnId === project.domain_id && targetLevelId === project.level_id) return;

    // Optimistic update
    setProjects((prev) =>
      prev.map((p) =>
        p.id === project.id ? { ...p, domain_id: targetColumnId, level_id: targetLevelId } : p
      )
    );

    api.updateProjectPlacement(project.id, targetColumnId, targetLevelId).catch(() => {
      api.getProjects().then((p) => setProjects(p as Project[]));
    });
  };

  useEffect(() => {
    Promise.all([
      api.getProjects() as Promise<Project[]>,
      api.getGridColumns('portfolio') as Promise<GridColumn[]>,
      api.getGridRows() as Promise<GridRow[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
    ]).then(([p, c, r, pipelineCols]) => {
      setProjects(p);
      setColumns(c);
      setRows(r);
      setPipelineColumns(pipelineCols);
      setLoading(false);
    });
  }, []);

  const stageColorInfo = useMemo(() => buildStageColors(pipelineColumns, accent), [pipelineColumns, accent]);
  const stageGradient = useMemo(() => buildStageGradient(stageColorInfo), [stageColorInfo]);

  // Every project ↔ project link, resolved once: declared on one side, derived
  // on the other (src/client/utils/projectLinks.ts).
  const linkIndex = useMemo(() => buildProjectLinkIndex(projects), [projects]);

  // Compute highlight map when a card is focused
  const highlightMap = useMemo(() => {
    if (focusedProjectId === null) return null;
    const focused = projects.find(p => p.id === focusedProjectId);
    if (!focused) return null;

    const { blockers, blocked } = linksFor(linkIndex, focusedProjectId);
    const needsSet = new Set<number>(blockers.map(l => l.projectId).filter((id): id is number => id != null));
    const enablesSet = new Set<number>(blocked.map(l => l.projectId).filter((id): id is number => id != null));

    const map = new Map<number, CardHighlight>();
    map.set(focusedProjectId, 'focused');

    for (const p of projects) {
      if (p.id === focusedProjectId) continue;
      const isNeeded = needsSet.has(p.id);
      const isEnabled = enablesSet.has(p.id);
      if (isNeeded && isEnabled) map.set(p.id, 'both');
      else if (isNeeded) map.set(p.id, 'needs');
      else if (isEnabled) map.set(p.id, 'enables');
      else map.set(p.id, 'dimmed');
    }
    return map;
  }, [focusedProjectId, projects, linkIndex]);

  const getHighlight = (projectId: number): CardHighlight => {
    if (!highlightMap) return null;
    return highlightMap.get(projectId) ?? 'dimmed';
  };

  const handleCardClick = (projectId: number) => {
    setFocusedProjectId(prev => prev === projectId ? null : projectId);
  };

  // Stage filter: 'active' (default — every stage except Done), 'all', or a
  // specific pipeline status id. Done projects are hidden by default but
  // remain selectable via the filter.
  const activeProjects = useMemo(() => {
    if (stageFilter === 'all') return projects;
    if (stageFilter === 'active') {
      const doneIds = getDoneStatusIds(pipelineColumns);
      return doneIds.size === 0 ? projects : projects.filter(p => !doneIds.has(p.status_id));
    }
    const sid = Number(stageFilter);
    return projects.filter(p => p.status_id === sid);
  }, [projects, pipelineColumns, stageFilter]);

  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return activeProjects;
    const q = searchTerm.toLowerCase();
    return activeProjects.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.project_code && p.project_code.toLowerCase().includes(q)) ||
      parseSponsorFirstName(p).toLowerCase().includes(q) ||
      parseSponsor(p).toLowerCase().includes(q)
    );
  }, [activeProjects, searchTerm]);

  const visibleColumns = useMemo(() => {
    if (!searchTerm.trim()) return columns;
    const colIds = new Set(filteredProjects.map(p => p.domain_id));
    return columns.filter(c => colIds.has(c.id));
  }, [columns, filteredProjects, searchTerm]);

  const visibleRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const rowIds = new Set(filteredProjects.map(p => p.level_id));
    return rows.filter(r => rowIds.has(r.id));
  }, [rows, filteredProjects, searchTerm]);

  if (loading) {
    return <div className="page-loading"><div className="spinner" /></div>;
  }

  const hasUnassignedDomain = filteredProjects.some(p => p.domain_id === null);
  const hasUnassignedLevel = filteredProjects.some(p => p.level_id === null);
  const totalCols = visibleColumns.length + (hasUnassignedDomain ? 1 : 0);

  const CardComponent = isAdmin ? DraggableProjectCard : ProjectCard;

  const renderRowCells = (rowFilter: (p: Project) => boolean, levelId: number | null) => (
    <>
      {visibleColumns.map((col) => {
        const cellProjects = filteredProjects.filter(
          (p) => p.domain_id === col.id && rowFilter(p)
        );
        return (
          <DroppableCell key={col.id} id={`cell-${levelId}-${col.id}`}>
            {cellProjects.map((project) => (
              <CardComponent
                key={project.id}
                project={project}
                highlight={getHighlight(project.id)}
                onCardClick={() => handleCardClick(project.id)}
                onInfo={(e) => { e.stopPropagation(); setDetailProject(project); }}
                isAdmin={isAdmin}
                stageGradient={stageGradient}
                cursorPercent={getStageCursorPercent(project.status_id, stageColorInfo)}
                secondaryStage={getSecondaryStage(project.status_id, stageColorInfo)}
                health={getExecutionHealth(project, pipelineColumns)}
                linkIndex={linkIndex}
              />
            ))}
          </DroppableCell>
        );
      })}
      {hasUnassignedDomain && (
        <DroppableCell id={`cell-${levelId}-null`}>
          {filteredProjects.filter(p => p.domain_id === null && rowFilter(p)).map((project) => (
            <CardComponent
              key={project.id}
              project={project}
              highlight={getHighlight(project.id)}
              onCardClick={() => handleCardClick(project.id)}
              onInfo={(e) => { e.stopPropagation(); setDetailProject(project); }}
              isAdmin={isAdmin}
              stageGradient={stageGradient}
              cursorPercent={getStageCursorPercent(project.status_id, stageColorInfo)}
              secondaryStage={getSecondaryStage(project.status_id, stageColorInfo)}
                health={getExecutionHealth(project, pipelineColumns)}
            />
          ))}
        </DroppableCell>
      )}
    </>
  );

  return (
    <div className="portfolio-page grid-page" onClick={() => setFocusedProjectId(null)}>
      <div className="toolbar page-toolbar" onClick={(e) => e.stopPropagation()}>
        <div className="toolbar-filters">
          <ProjectSearchBox />
          <FilterSelect
            label={t('portfolio.stage')}
            multiple={false}
            clearable={false}
            neutral="active"
            selected={[stageFilter]}
            options={[
              { value: 'active', label: t('portfolio.stageActive') },
              { value: 'all', label: t('portfolio.stageAll') },
              ...pipelineColumns.map((c) => ({ value: String(c.id), label: c.label })),
            ]}
            onChange={(vals) => setStageFilter(String(vals[0] ?? 'active'))}
          />
        </div>
      </div>
      {searchTerm.trim() && filteredProjects.length === 0 ? (
        <div className="grid-no-results">
          <div className="empty-state">
            <div className="empty-state-title">{t('portfolio.noMatchTitle')}</div>
            <div className="empty-state-desc">{t('portfolio.noMatchDesc', { q: searchTerm })}</div>
          </div>
        </div>
      ) : (
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="matrix-grid-container">
          <div
            className="matrix-grid"
            style={{ gridTemplateColumns: `48px repeat(${totalCols}, minmax(260px, 1fr))` }}
          >
            <div className="matrix-grid-corner" />

            {visibleColumns.map((col) => (
              <div key={col.id} className="matrix-grid-col">
                {col.label}
              </div>
            ))}
            {hasUnassignedDomain && (
              <div className="matrix-grid-col">{t('portfolio.unassigned')}</div>
            )}

            {visibleRows.map((row) => (
              <>
                <div key={`row-${row.id}`} className="matrix-grid-row">
                  {row.label}
                </div>
                {renderRowCells((p) => p.level_id === row.id, row.id)}
              </>
            ))}

            {hasUnassignedLevel && (
              <>
                <div className="matrix-grid-row">{t('portfolio.unassigned')}</div>
                {renderRowCells((p) => p.level_id === null, null)}
              </>
            )}
          </div>
        </div>

        <DragOverlay>
          {activeProject && (
            <div data-dnd-overlay>
              <ProjectCard
                project={activeProject}
                highlight={null}
                onCardClick={() => {}}
                onInfo={() => {}}
                isAdmin={false}
                stageGradient={stageGradient}
                cursorPercent={getStageCursorPercent(activeProject.status_id, stageColorInfo)}
                secondaryStage={getSecondaryStage(activeProject.status_id, stageColorInfo)}
                health={getExecutionHealth(activeProject, pipelineColumns)}
                linkIndex={linkIndex}
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>
      )}

      {detailProject && (
        <DetailPanel
          project={detailProject}
          projects={projects}
          onClose={() => setDetailProject(null)}
        />
      )}
    </div>
  );
}
